import React, { useState, useEffect, useRef } from "react";
import PageHeader from "../../components/PageHeader/PageHeader";
import { useNavigate, useLocation } from "react-router-dom";
import { patch, post, put } from "../../Services/ApiHandler";
import { URLS } from "../../Services/Constant";
import "../Videos/AddVideo.css";
import "./PromotionBanner.css";
import { toast } from "react-toastify";
import Loader from "../../components/Loader";
import { getBannerImageUrl } from "./PromotionBanner";

const MAX_IMAGE_SIZE_MB = 5;

const PromotionBannerAdd = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data, mode, page } = location.state || {};
  const isViewMode = mode === "view";
  const isEditMode = mode === "edit";
  const [loading, setLoading] = useState(false);
  const [bannerName, setBannerName] = useState("");
  const [bannerImage, setBannerImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (data && (isViewMode || isEditMode)) {
      setBannerName(data?.title || "");
      setPreviewUrl(getBannerImageUrl(data?.image_url));
    }
  }, [data, isViewMode, isEditMode]);

  // release the object URL created for a locally picked file
  useEffect(() => {
    return () => {
      if (previewUrl.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const goBack = () =>
    navigate("/promotion-banner", { state: { page: page || 1 } });

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file");
      return;
    }
    if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
      toast.error(`Image size should be less than ${MAX_IMAGE_SIZE_MB}MB`);
      return;
    }

    setBannerImage(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!bannerName.trim()) {
      toast.error("Please enter banner name");
      return;
    }
    // on edit the existing image is kept unless a new one is picked
    if (!bannerImage && !isEditMode) {
      toast.error("Kindly upload the banner image");
      return;
    }

    const payload = new FormData();
    payload.append("title", bannerName.trim());
    if (bannerImage) {
      payload.append("image", bannerImage);
    }

    setLoading(true);
    const request = isEditMode
      ? patch(URLS.PROMOTION_BANNER_UPDATE + data?.id + "/", payload)
      : post(URLS.PROMOTION_BANNER_CREATE, payload);

    request
      .then((res) => {
        setLoading(false);
        if (res?.status === "success") {
          toast.success(
            res?.message ||
              (isEditMode
                ? "Banner updated successfully"
                : "Banner added successfully"),
          );
          goBack();
        }
      })
      .catch((err) => {
        setLoading(false);
        console.log("err", err);
      });
  };

  return (
    <>
      <PageHeader
        title={
          isViewMode ? "View Banner" : isEditMode ? "Edit Banner" : "Add Banner"
        }
        showBackButton={true}
        onBackClick={goBack}
      />
      <div className="add-admin-container">
        <form className="add-admin-form" onSubmit={handleSubmit} noValidate>
          <div className="form-row">
            <div className="form-group">
              <label>Banner Name</label>
              <input
                type="text"
                value={bannerName}
                onChange={(e) => setBannerName(e.target.value)}
                placeholder="Enter Banner Name"
                disabled={isViewMode}
              />
            </div>

            {isViewMode && (
              <div className="form-group">
                <label>Status</label>
                <input
                  type="text"
                  value={data?.is_active === true ? "Active" : "Inactive"}
                  disabled
                />
              </div>
            )}
          </div>

          <div className="form-group banner-upload-group">
            <label>Banner Image</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handleImageChange}
            />

            {previewUrl ? (
              <div className="banner-preview">
                <img src={previewUrl} alt="banner preview" />
                {!isViewMode && (
                  <button
                    type="button"
                    className="banner-change-btn"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Change Image
                  </button>
                )}
              </div>
            ) : isViewMode ? (
              <p className="mb-0">No image uploaded</p>
            ) : (
              <div
                className="banner-upload-box"
                onClick={() => fileInputRef.current?.click()}
              >
                <span>Click to upload banner image</span>
                <small>JPG / PNG, max {MAX_IMAGE_SIZE_MB}MB</small>
              </div>
            )}
          </div>

          {!isViewMode && (
            <div className="form-actions">
              <button type="button" className="btn-back" onClick={goBack}>
                Back
              </button>
              <button type="submit" className="btn-save">
                {isEditMode ? "Update" : "Save"}
              </button>
            </div>
          )}
        </form>
        {loading && <Loader />}
      </div>
    </>
  );
};

export default PromotionBannerAdd;
